// Who sees how much of the sidebar's Appointments group (PRD 121 §4.12) — the
// one rule, so the group and every later row (Staff, Locations, Menu, PRD 121
// Waves 2–3) cannot disagree about a business. fieldServiceNavAccess's shape.

import type { BusinessModulesState, ModuleNavAccess } from '../../services/businessModulesStore';
import { PERSONAL_SERVICES_RELEASED } from './release';

/**
 * The sidebar's answer for the Appointments group.
 *
 *   - Unknown draws nothing: an unreleased paid module is never guessed into
 *     anybody's menu (businessModulesStore).
 *   - A holder sees every row — today, design partners on an admin grant.
 *   - WHILE UNRELEASED, nobody else sees a row at all: web merges deploy prod,
 *     and a business without the grant must find no trace of the module.
 *   - After release, a business that set it up and then lapsed keeps every row,
 *     read-only (PRD 42 Rule 4), and one that never set it up sees one row,
 *     the Overview, whose page carries the banner. An unknown set-up still
 *     draws that one row, since both answers draw it.
 */
export function personalServicesNavAccess(state: BusinessModulesState): ModuleNavAccess {
  if (!state.modules) return 'none';
  if (state.modules.personal_services === true) return 'all';
  if (!PERSONAL_SERVICES_RELEASED) return 'none';
  if (state.personalServicesSetUp === true) return 'all';
  return 'front_door';
}

/**
 * The module's two roles (PRD 121 §4.10) — the api's
 * BusinessMembership::PERSONAL_SERVICES_ROLES. Strings, so a role check reads
 * the same before and after BusinessRole names them.
 */
export const PERSONAL_SERVICES_ROLES: readonly string[] = ['front_desk', 'provider'];

export const isPersonalServicesRole = (role: string | null | undefined): boolean =>
  role != null && PERSONAL_SERVICES_ROLES.includes(role);

/**
 * The providers census line (PRD 121 §4.11, Wave 4) — the api's
 * Appointments::CensusController CENSUS_ROLES. Anyone else is never sent to
 * appointments/census: a restricted role's refusal is role_forbidden, which
 * replaces the whole Staff page.
 */
export const maySeeProvidersCensus = (role: string | null | undefined): boolean =>
  role === 'owner' || role === 'accountant' || role === 'bookkeeper' || role === 'front_desk';

/** CENSUS_DETAIL_ROLES: who sees each staff member's "Counts toward billing", and why. */
export const maySeeProvidersBilling = (role: string | null | undefined): boolean =>
  role === 'owner' || role === 'accountant' || role === 'bookkeeper';
